import {
	fetchOfferingInfo,
	getCurrentPlan,
} from "@/comps/subscription-popup"
import useAuth from "@/utils/client-auth"
import { scrollToSection } from "@/utils/scrollto"
import { useWallet } from "@/utils/wallet"
import clsx from "clsx"
import { useTranslation } from "next-i18next"
import { useQuery } from "react-query"

export default function SubscriptionDetails() {
	const { t } = useTranslation()
	const { userInfo } = useAuth()

	const offeringInfo = useQuery({
		queryKey: ["offering-info", userInfo?.entitlements],
		queryFn: fetchOfferingInfo(),
		staleTime: Infinity,
	})

	const currentPlan =
		userInfo && offeringInfo.data
			? getCurrentPlan(
					userInfo.entitlements,
					offeringInfo.data.result,
			  )
			: { id: "free", title: "Free", weight: -1 }

	const wallet = useWallet()

	const targetDate = new Date(
		wallet?.renew_info?.date ?? "",
	).getTime()
	const currentDate = new Date().getTime()

	const differenceMs = targetDate - currentDate

	const daysLeft = Math.ceil(
		differenceMs / (1000 * 60 * 60 * 24),
	)

	return (
		<div
			className={clsx(
				" flex w-full flex-col items-center justify-center gap-5 px-4 transition-all duration-300",
				!wallet || !wallet.renew_info
					? "pointer-events-none h-[0px] overflow-hidden"
					: "mt-[50px] h-[340px] tablet:h-[320px] desktop:h-[325px]",
			)}>
			<h2 className="text-[18px] font-700 text-blue-900 tablet:text-[20px] desktop:text-[28px]">
				{t("txt_subscription_details")}
			</h2>

			{wallet && wallet.renew_info && (
				<div className="flex w-full flex-col items-center justify-center gap-1 desktop:w-[775px]">
					<div
						className={clsx(
							"flex w-full flex-col gap-5 rounded-[12px] bg-color-cell px-6 py-[18px] desktop:w-[775px]",
							"[box-shadow:0px_5px_12px_0px_rgba(183,183,183,0.12)] dark:[box-shadow:unset]",
						)}>
						<div className="flex flex-row items-center gap-[22px]">
							<p className="text-[16px] font-500 text-blue-600">
								{t("txt_current_plan")}
							</p>
							<p className="text-[16px] font-500 text-blue-800">
								{currentPlan.title}
							</p>
						</div>
						<div className="h-[1px] w-full bg-color-separator" />
						<div className="flex flex-row items-center gap-[22px]">
							<p className="text-[16px] font-500 text-blue-600">
								{t("txt_renew_date")}
							</p>
							<p className="text-[16px] font-500 text-blue-800">
								{wallet.renew_info.cancelled
									? "-"
									: new Date(
											wallet.renew_info.date,
									  ).toLocaleDateString("en-US", {
											year: "numeric",
											month: "long",
											day: "numeric",
									  })}
							</p>
						</div>
						<div className="h-[1px] w-full bg-color-separator" />
						<div className="flex flex-col gap-3 tablet:gap-2">
							<div className="flex flex-col items-start justify-between gap-1 tablet:flex-row tablet:items-center">
								<p className="text-[16px] font-500 text-blue-600">
									{t("txt_available_tokens")}
								</p>
								<p className="text-[12px] font-400 text-blue-500">
									{t("fs_tokens_left", {
										temporary_tokens:
											(
												wallet.temporary_tokens +
												wallet.permanent_tokens
											).toLocaleString() ?? 0,
										tokens:
											(
												wallet.renew_info.tokens +
												wallet.permanent_tokens
											).toLocaleString() ?? 0,
										days: daysLeft,
									})}
								</p>
							</div>

							<div className="relative flex h-1 w-full overflow-hidden rounded-[10px] bg-blue-200">
								<div
									style={{
										width: `${
											((wallet.temporary_tokens +
												wallet.permanent_tokens) /
												(wallet.renew_info.tokens +
													wallet.permanent_tokens)) *
											100
										}%`,
									}}
									className={clsx(
										"absolute left-0 top-0 h-full transition-all",
										((wallet.temporary_tokens +
											wallet.permanent_tokens) /
											(wallet.renew_info.tokens +
												wallet.permanent_tokens)) *
											100 >
											15
											? "bg-color-success"
											: "bg-color-error",
									)}></div>
							</div>

							<div className="flex w-full flex-row items-center justify-end">
								<button
									onClick={(e) => {
										e.preventDefault()
										scrollToSection("pricing")()
									}}
									className="hover:bg-primary-600/10 flex flex-row items-center gap-1 rounded-[4px] px-2 py-1 transition-colors">
									<div className="relative h-[10px] w-[10px]">
										<div className="absolute left-0 top-1/2 h-[1.5px] w-full rounded-sm bg-primary-500" />
										<div className="absolute left-0 top-1/2 h-[1.5px] w-full rotate-90 rounded-sm bg-primary-500" />
									</div>
									<p className="text-[12px] font-600 text-primary-500">
										{t("txt_add_tokens")}
									</p>
								</button>
							</div>
						</div>
					</div>

					<div className="flex w-full items-start justify-start px-1">
						<button
							onClick={(e) => {
								e.preventDefault()
								scrollToSection("pricing")()
							}}
							className="text-[14px] font-500 text-blue-500">
							{t("txt_change_plan")}
						</button>
					</div>
				</div>
			)}
		</div>
	)
}
